/**
 * Command Center — the executive real-time operational hub.
 *
 * Full-screen dark-mode institutional dashboard auto-refreshing every 10 s.
 * Sections:
 *   A) KPIs live
 *   B) Institutional alerts
 *   C) Risk heatmap (country + category)
 *   D) Operational timeline
 *   E) Active modules
 *   F) Executive quick actions
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Activity, AlertTriangle, Boxes, FileText, Flame, Handshake, Home, Loader2, Map,
  Package, ShieldAlert, ShoppingCart, Users, Zap, Globe2,
  ArrowRight, RefreshCcw, AlertOctagon, TrendingUp,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const REFRESH_MS = 10000;

const SEVERITY_STYLES = {
  info: 'bg-sky-500/20 text-sky-200 border-sky-500/40',
  warning: 'bg-amber-500/20 text-amber-200 border-amber-500/40',
  error: 'bg-rose-500/20 text-rose-200 border-rose-500/40',
  critical: 'bg-red-600/30 text-red-100 border-red-500/60',
};

const CommandCenter = () => {
  const navigate = useNavigate();
  const token = useMemo(() => localStorage.getItem('admin_token'), []);
  const [kpis, setKpis] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [heatmap, setHeatmap] = useState({ by_country: [], by_category: [] });
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    const h = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const [kpi, al, tl, hm] = await Promise.all([
        axios.get(`${API_URL}/api/command-center/kpis`, h),
        axios.get(`${API_URL}/api/command-center/alerts`, h),
        axios.get(`${API_URL}/api/command-center/timeline`, h),
        axios.get(`${API_URL}/api/command-center/heatmap`, h),
      ]);
      setKpis(kpi.data);
      setAlerts(al.data || []);
      setTimeline(tl.data || []);
      setHeatmap(hm.data || { by_country: [], by_category: [] });
      setLastRefresh(new Date());
    } catch (_) { /* keep last data */ }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1220] flex items-center justify-center text-slate-300">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading Command Center…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0B1220] via-[#0F172A] to-[#020617] text-slate-100 p-6" data-testid="command-center-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-[#FF6B35] to-[#F97316] flex items-center justify-center shadow-lg shadow-orange-500/30">
            <ShieldAlert className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Command Center</h1>
            <p className="text-xs text-slate-400">Real-time operational hub · auto-refresh 10 s</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/admin/dashboard')}
                  className="text-xs text-slate-400 hover:text-white flex items-center gap-1"
                  data-testid="command-center-back-btn">
            <Home className="w-4 h-4" /> Dashboard
          </button>
          <button onClick={load}
                  className="text-xs flex items-center gap-1 px-3 py-1.5 rounded border border-slate-700 hover:bg-slate-800"
                  data-testid="command-center-refresh-btn">
            <RefreshCcw className="w-3 h-3" /> Refresh
          </button>
          {lastRefresh && (
            <span className="text-[11px] text-slate-500">
              {lastRefresh.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      {/* A — KPIs */}
      <KPIsGrid kpis={kpis} />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 mt-6">
        {/* B — Alerts */}
        <div className="xl:col-span-1 bg-slate-900/60 border border-slate-800 rounded-xl p-4" data-testid="cc-alerts-panel">
          <SectionHeader icon={AlertOctagon} title="Institutional Alerts" count={alerts.length} />
          {alerts.length === 0 ? (
            <p className="text-xs text-slate-500">No open alerts.</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
              {alerts.map((a) => (
                <div key={a.id}
                     className={`border rounded-lg p-2.5 text-xs ${SEVERITY_STYLES[a.severity] || SEVERITY_STYLES.warning}`}
                     data-testid={`cc-alert-${a.id}`}>
                  <div className="flex items-start gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{a.title}</div>
                      <div className="opacity-90 text-[11px] leading-tight mt-0.5">{a.message}</div>
                      <div className="text-[10px] opacity-60 mt-1 uppercase">{a.kind}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* D — Timeline */}
        <div className="xl:col-span-1 bg-slate-900/60 border border-slate-800 rounded-xl p-4" data-testid="cc-timeline-panel">
          <SectionHeader icon={Activity} title="Operational Timeline" count={timeline.length} />
          <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
            {timeline.map((ev, i) => (
              <div key={i} className="flex items-start gap-2 text-xs border-l-2 border-[#023E8A] pl-3 pb-1.5">
                <TimelineIcon kind={ev.kind} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-200 truncate">{ev.title}</div>
                  <div className="text-[10px] text-slate-500 uppercase">
                    {ev.kind} {ev.at ? `· ${formatDate(ev.at)}` : ''}
                  </div>
                </div>
              </div>
            ))}
            {timeline.length === 0 && <p className="text-xs text-slate-500">No recent events.</p>}
          </div>
        </div>

        {/* C — Heatmap */}
        <div className="xl:col-span-1 bg-slate-900/60 border border-slate-800 rounded-xl p-4" data-testid="cc-heatmap-panel">
          <SectionHeader icon={Map} title="Risk Heatmap" count={heatmap.by_country.length + heatmap.by_category.length} />
          <div className="space-y-3">
            <HeatGroup title="By Country" icon={Globe2} rows={heatmap.by_country.map((r) => ({
              label: r.country, score: r.avg_score, count: r.count,
            }))} />
            <HeatGroup title="By Product Category" icon={Package} rows={heatmap.by_category.map((r) => ({
              label: r.category_id || 'Uncategorised', score: r.avg_score, count: r.count,
            }))} />
          </div>
        </div>
      </div>

      {/* E — Quick Actions (Executive) */}
      <div className="mt-5 bg-slate-900/60 border border-slate-800 rounded-xl p-4" data-testid="cc-quick-actions">
        <SectionHeader icon={Zap} title="Executive Quick Actions" />
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2">
          <QuickAction label="Create PO" icon={ShoppingCart} onClick={() => navigate('/admin/procurement/purchase-orders')} testId="qa-po" />
          <QuickAction label="Create Invoice" icon={FileText} onClick={() => navigate('/admin/finance/invoices/create')} testId="qa-invoice" />
          <QuickAction label="Create Product" icon={Package} onClick={() => navigate('/admin/products')} testId="qa-product" />
          <QuickAction label="Start KYC" icon={ShieldAlert} onClick={() => navigate('/admin/risk/kyc')} testId="qa-kyc" />
          <QuickAction label="Run Sanctions" icon={Flame} onClick={() => navigate('/admin/risk/scoring')} testId="qa-sanctions" />
          <QuickAction label="Supplier Scorecard" icon={TrendingUp} onClick={() => navigate('/admin/procurement/suppliers/ranking')} testId="qa-scorecard" />
          <QuickAction label="Counterparties" icon={Users} onClick={() => navigate('/admin/counterparties')} testId="qa-counterparties" />
          <QuickAction label="Finance Dashboard" icon={Home} onClick={() => navigate('/admin/finance/dashboard')} testId="qa-finance" />
          <QuickAction label="Procurement Dashboard" icon={Boxes} onClick={() => navigate('/admin/procurement/dashboard')} testId="qa-procurement" />
          <QuickAction label="CRM Deals" icon={Handshake} onClick={() => navigate('/admin/crm/jv-pipeline')} testId="qa-deals" />
          <QuickAction label="PDF Gallery" icon={FileText} onClick={() => navigate('/admin/cms/pdf-preview-gallery')} testId="qa-pdf-gallery" />
          <QuickAction label="Back to Dashboard" icon={Home} onClick={() => navigate('/admin/dashboard')} testId="qa-dashboard" />
        </div>
      </div>
    </div>
  );
};


// ============================================ sub-components
const KPIsGrid = ({ kpis }) => {
  if (!kpis) return null;
  const items = [
    { label: 'POs Open', value: kpis.pos_open, icon: ShoppingCart, color: 'text-sky-400' },
    { label: 'GRNs (Month)', value: kpis.grns_month, icon: Package, color: 'text-emerald-400' },
    { label: 'Invoices Pending', value: kpis.invoices_pending, icon: FileText, color: 'text-violet-400' },
    { label: 'KYC Pending', value: kpis.kyc_pending, icon: ShieldAlert, color: 'text-amber-400' },
    { label: 'Sanctions Hits', value: kpis.sanctions_hits_open, icon: Flame, color: 'text-rose-400' },
    { label: 'Active Alerts', value: kpis.active_alerts, icon: AlertOctagon, color: 'text-red-400' },
    { label: 'Counterparties', value: kpis.counterparties_total, icon: Users, color: 'text-cyan-400' },
    { label: 'Products Active', value: kpis.products_active, icon: Boxes, color: 'text-lime-400' },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3" data-testid="cc-kpis-grid">
      {items.map((it, i) => {
        const Icon = it.icon;
        return (
          <div key={i} className="bg-slate-900/70 border border-slate-800 rounded-lg p-3" data-testid={`cc-kpi-${i}`}>
            <div className={`flex items-center gap-1.5 ${it.color} text-xs uppercase font-semibold mb-1`}>
              <Icon className="w-3.5 h-3.5" /> {it.label}
            </div>
            <div className="text-2xl font-bold tabular-nums">{it.value ?? '—'}</div>
          </div>
        );
      })}
    </div>
  );
};


const SectionHeader = ({ icon: Icon, title, count }) => (
  <div className="flex items-center gap-2 mb-3">
    <Icon className="w-4 h-4 text-slate-400" />
    <h3 className="text-sm font-bold uppercase tracking-wide text-slate-200">{title}</h3>
    {count !== undefined && (
      <span className="text-[10px] text-slate-500 bg-slate-800 px-1.5 py-0.5 rounded">{count}</span>
    )}
  </div>
);


const HeatGroup = ({ title, icon: Icon, rows }) => (
  <div>
    <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold text-slate-400 mb-1.5">
      <Icon className="w-3 h-3" /> {title}
    </div>
    {rows.length === 0 ? (
      <p className="text-[11px] text-slate-600">No data yet — run risk scoring.</p>
    ) : (
      <div className="space-y-1">
        {rows.slice(0, 6).map((r, i) => {
          const pct = Math.min(100, Math.max(0, r.score));
          const color = r.score >= 75 ? 'bg-red-500' : r.score >= 50 ? 'bg-orange-500' : r.score >= 25 ? 'bg-amber-500' : 'bg-emerald-500';
          return (
            <div key={i} className="text-[11px]">
              <div className="flex items-center justify-between">
                <span className="text-slate-300 truncate flex-1">{r.label}</span>
                <span className="font-mono text-slate-400">{r.score}</span>
              </div>
              <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden mt-0.5">
                <div className={`${color} h-full`} style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    )}
  </div>
);


const TimelineIcon = ({ kind }) => {
  const map = {
    po: ShoppingCart, invoice: FileText, grn: Package,
    product: Boxes, alert: AlertTriangle,
  };
  const Icon = map[kind] || Activity;
  return <Icon className="w-3.5 h-3.5 text-slate-400 mt-0.5" />;
};


const QuickAction = ({ label, icon: Icon, onClick, testId }) => (
  <button onClick={onClick}
          className="flex items-center gap-2 p-2.5 bg-slate-800/50 border border-slate-700 rounded-lg hover:bg-slate-700 text-left transition-colors"
          data-testid={testId}>
    <Icon className="w-4 h-4 text-[#FF6B35]" />
    <span className="text-xs font-medium flex-1 truncate">{label}</span>
    <ArrowRight className="w-3 h-3 text-slate-500" />
  </button>
);


const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-GB', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' });
  } catch (_) { return iso; }
};


export default CommandCenter;
